<script setup lang="ts">
    import { useRoute, useRouter } from 'vue-router';
    import EchoButton from '../components/EchoButton.vue';
    import IconButton from '../components/IconButton.vue';
    import SettingsPageLayout from '../components/settings/SettingsPageLayout.vue';
    import { __ } from '../stores/expressions';
    import EditCoachingFormForm from '../components/coaching/EditCoachingFormForm.vue';
    import { useEditCoachingFormStore } from '../stores/edit-coaching-form';
    import { storeToRefs } from 'pinia';
    import {
        useGetCoachingForm,
        useMutateUpdateCoachingForm,
    } from '../services/api/coachingForm/coachingFormQueries';
    import { transformCoachingFormDraftToRequest } from '../services/api/coachingForm/utils';
    import { computed, onMounted, provide } from 'vue';
    import { CheckIcon, ChevronLeftIcon, XIcon } from '@lucide/vue';
    import { VALIDATION_SCOPE_KEY } from '../composables/useValidationErrors';
    import { COACHING_FORM_VALIDATION_SCOPE } from '../services/api/coachingForm/coachingFormApi';

    provide(VALIDATION_SCOPE_KEY, COACHING_FORM_VALIDATION_SCOPE);

    const { loadForm } = useEditCoachingFormStore();
    const { formChangeLog, formDraft } = storeToRefs(useEditCoachingFormStore());

    const router = useRouter();
    const route = useRoute();

    const coachingFormId = computed(() => route.params.id as string | null);

    const { mutateAsync: updateCoachingForm, isPending } =
        useMutateUpdateCoachingForm(coachingFormId);

    const saveCoachingForm = async () => {
        if (!formDraft.value || !coachingFormId.value) {
            return;
        }

        const form = await updateCoachingForm({
            formId: coachingFormId.value,
            changes: formChangeLog.value,
            ...transformCoachingFormDraftToRequest(formDraft.value),
        });

        router.push('/settings/manage-forms/' + form.id);
    };

    const { data: editCoachingFormData, refetch: refetchCoachingForm } =
        useGetCoachingForm(coachingFormId);

    onMounted(async () => {
        await router.isReady();

        if (route.params.id) {
            await refetchCoachingForm();
        }

        if (editCoachingFormData.value) {
            loadForm(editCoachingFormData.value);
        }
    });
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-xl">{{ __('settings-manage-forms') }}</h1>
        </template>
        <template #topbar>
            <EchoButton
                variant="primary"
                :disabled="formChangeLog.length === 0"
                :loading="isPending"
                @click="saveCoachingForm()"
            >
                {{ __('edit-coaching-form-save-form') }}
                <template #icon-right><CheckIcon /></template>
            </EchoButton>

            <EchoButton variant="secondary" :route="'/settings/manage-forms/' + route.params.id">
                {{ __('edit-coaching-form-cancel') }}
                <template #icon-right><XIcon /></template>
            </EchoButton>
        </template>
        <template #content>
            <div class="flex items-center gap-4 max-w-235 mx-auto">
                <IconButton
                    area-size="size-10.5"
                    size="size-5"
                    class="border border-gray-light rounded-full hover:bg-gray-lightest"
                    :tooltip="__('all-forms')"
                    route="/settings/manage-forms"
                >
                    <ChevronLeftIcon class="size-4.5" />
                </IconButton>
                <div class="flex items-center gap-2 min-w-0">
                    <RouterLink
                        to="/settings/manage-forms"
                        class="text-body text-gray-medium hover:text-primary-beet"
                    >
                        {{ __('all-forms') }}
                    </RouterLink>
                    <span class="text-gray-light">/</span>
                    <span class="text-body-bold truncate">{{ formDraft?.title }}</span>
                </div>
            </div>
            <div class="pt-6">
                <EditCoachingFormForm />
            </div>
        </template>
    </SettingsPageLayout>
</template>
